import { arrayUnion, Timestamp } from 'firebase/firestore';
import { getDownloadURL } from 'firebase/storage';
import {
  chain,
  cloneDeep,
  concat,
  find,
  get,
  includes,
  isArray,
  isDate,
  isEmpty,
  isFunction,
  isNumber,
  isObject,
  join,
  map,
  mapValues,
  reject,
  replace,
  sortBy,
  without,
} from 'lodash';
import { mapActions, mapState } from 'pinia';
import { uid } from 'quasar';
import { CATEGORY_IDENTIFIERS, SESSION_NOTE_FIELDS } from 'src/constants/dynamicFields';
import { INTEGRATION_NAMES } from 'src/constants/integrations';
import MessagesDB from 'src/fb/messages-db';
import GenericIndexedDb from 'src/indexed-db/generic-indexedDb';
import AppointmentsMixin from 'src/mixins/AppointmentsMixin';
import Auth from 'src/mixins/Auth';
import ReportImageUpload from 'src/mixins/ReportImageUpload';
import TemplateMixin from 'src/mixins/TemplateMixin';
import Time from 'src/mixins/Time';
import Toolbox from 'src/mixins/Toolbox';
import { auth } from 'src/store/auth';
import { broadcastChannel } from 'src/store/broadcastChannel';
import { firebaseUploader } from 'src/store/firebaseUploader';
import { network } from 'src/store/network';
import AppUtils from 'src/utils/AppUtils';
import { logEvent } from 'src/utils/LogEvent';
import {
  FILES_TO_UPLOAD_COLLECTION,
  findImagesToDeleteFromUploadingQueue,
  replaceLocalImagesWithStorage,
} from 'src/utils/OfflineAccessUtils';

/**
 * @typedef {{
 * category: CategoryIdentifier,
 * token: string,
 * label: string,
 * id: string,
 * tokenType?: string,
 * hint?: string | null,
 * type?: string,
 * canEdit?: boolean,
 * prompt?: string | null,
 * completable?: boolean,
 * allowMultiLine?: boolean,
 * excludeIntegrations?: string[],
 * warning?: () => string,
 * format?: (data: unknown) => unknown,
 * isDisabled?: () => boolean,
 *}} Field
 * */

/**
 * @typedef {{
 * categoryIdentifier: CategoryIdentifier,
 * categoryLabel: string,
 * fields: Field[],
 *}} FieldCategory
 * */

/**
 * @typedef {{
*    originalNotes?: string | null,
*    updatedNotes?: string | null,
*    draftSave?: boolean,
*    hardUpdate?: boolean,
*    sessionActivityId?: string,
* }} SavingNotesOptions
* @property {string | null} [originalNotes] - original notes
* @property {string | null} [updatedNotes] - updated notes
* @property {boolean} [draftSave] - changes the buttons loading status behavior
* @property {boolean} [hardUpdate] - update metadata of all images in notes
* @property {string | null} [sessionActivityId] - Id of the session activity
*/

const SESSION_ACTIVITY_COLLECTION = 'sessionActivities';
const MS_IN_24_HOURS = 24 * 60 * 60 * 1000;

export default {
  mixins: [Auth, AppointmentsMixin, ReportImageUpload, Toolbox, TemplateMixin, Time],

  data() {
    return {
      sessionSignatureLoadingStatus: {
        signing: false,
        claims: false,
      },

      sessionNoteFieldCategoryPresets: [
        {
          categoryIdentifier: CATEGORY_IDENTIFIERS.CLIENT,
          categoryLabelTranslation: this.$t('templates.categories.client'),
        },
        {
          categoryIdentifier: CATEGORY_IDENTIFIERS.STAFF_MEMBER,
          categoryLabelTranslation: this.$t('templates.categories.staffMember'),
        },
        {
          categoryIdentifier: CATEGORY_IDENTIFIERS.SESSION,
          categoryLabelTranslation: this.$t('templates.categories.session'),
        },
        {
          categoryIdentifier: CATEGORY_IDENTIFIERS.SESSION_NOTE,
          categoryLabelTranslation: this.$t('templates.categories.sessionNotes'),
        },
        {
          categoryIdentifier: CATEGORY_IDENTIFIERS.APPOINTMENT,
          categoryLabelTranslation: this.$t('templates.categories.appointment'),
        },
        {
          categoryIdentifier: CATEGORY_IDENTIFIERS.USER,
          categoryLabelTranslation: this.$t('templates.categories.user'),
        },
      ],

      /**
       * ## Warning!
       * Adding a new field to this list will additionally require:
       *   – adding that field’s token to the `predefinedFields` array of the `copyNoteTemplate` cloud function
       *   – adding that field’s id to the `SESSION_NOTE_FIELDS` constant in `constants/dynamicFields`
       * @type {Field[]}
       */
      fixedSessionNoteFieldPresets: [
        {
          token: 'session.sessionName',
          id: 'sessionName',
          label: this.$t('templates.session.sessionName'),
          hint: null,
          category: 'session',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
        },
        {
          token: 'session.sessionActivityId',
          id: 'sessionActivityId',
          label: this.$t('templates.session.sessionActivityId'),
          hint: null,
          category: 'session',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
        },
        {
          token: 'session.sessionDate',
          id: 'sessionDate',
          label: this.$t('templates.session.date'),
          hint: null,
          category: 'session',
          type: 'date',
          prompt: false,
          promptGroup: 'sessionTime',
        },
        {
          token: `${CATEGORY_IDENTIFIERS.SESSION}.${SESSION_NOTE_FIELDS.SESSION_START_TIME}`,
          id: SESSION_NOTE_FIELDS.SESSION_START_TIME,
          label: this.$t('templates.session.startTime'),
          hint: null,
          category: CATEGORY_IDENTIFIERS.SESSION,
          type: 'time',
          prompt: true,
          deferPrompt: true,
          promptGroup: 'sessionTime',
          isDisabled: (session, sessionActivity) => (this.disableEditTimestamps(sessionActivity)),
          adjustGroupField: (value, fields) => (
            this.adjustGroupField(value, fields, SESSION_NOTE_FIELDS.SESSION_FINISH_TIME)
          ),
          warning: (value, session, sessionActivity, appointment) => {
            // Show warning if entered start time is more than 1 hour before/after scheduled start time
            if (!(isDate(value))
              || !(get(appointment, 'scheduledStartTimestamp') instanceof Timestamp)
              || (this.disableEditTimestamps(sessionActivity))) {
              return;
            }

            const scheduledStartDate = appointment.scheduledStartTimestamp.toDate();
            const startTimeDiff = scheduledStartDate - value;

            if (Math.abs(startTimeDiff) > (60 * 60 * 1000)) {
              return this.$t(
                'templates.session.warningStartTime',
                {
                  scheduledStartTime: this.getLocaleTimeString(scheduledStartDate, {
                    options: { hour: 'numeric', minute: '2-digit' },
                  }),
                },
              );
            }
          },

          format: (ts) => (isDate(ts)
            ? this.getLocaleTimeString(ts, { options: { hour: 'numeric', minute: '2-digit' } })
            : ''),
        },
        {
          token: `${CATEGORY_IDENTIFIERS.SESSION}.${SESSION_NOTE_FIELDS.SESSION_FINISH_TIME}`,
          id: SESSION_NOTE_FIELDS.SESSION_FINISH_TIME,
          label: this.$t('templates.session.finishTime'),
          hint: null,
          category: CATEGORY_IDENTIFIERS.SESSION,
          type: 'time',
          prompt: true,
          deferPrompt: true,
          promptGroup: 'sessionTime',
          isDisabled: (session, sessionActivity) => (this.disableEditTimestamps(sessionActivity)),
          adjust: (endTimestamp, props) => {
            const { startTimestamp } = (props || {});

            return this.adjustEndTime(endTimestamp, startTimestamp);
          },

          warning: (value, session, sessionActivity, appointment) => {
            // Show warning if entered start time is more than 1 hour before/after scheduled start time
            if (!(isDate(value))
              || !(get(appointment, 'scheduledStartTimestamp') instanceof Timestamp)
              || (this.disableEditTimestamps(sessionActivity))) {
              return;
            }

            const scheduledStartDate = appointment.scheduledStartTimestamp.toDate();
            const scheduledEndDate = this.addToDate(
              scheduledStartDate,
              { seconds: appointment.duration || 0 },
            );

            const endTimeDiff = scheduledEndDate - value;

            if (Math.abs(endTimeDiff) > (60 * 60 * 1000)) {
              return this.$t(
                'templates.session.warningEndTime',
                {
                  scheduledEndTime: this.getLocaleTimeString(scheduledEndDate, {
                    options: { hour: 'numeric', minute: '2-digit' },
                  }),
                },
              );
            }
          },

          format: (ts) => (isDate(ts)
            ? this.getLocaleTimeString(ts, { options: { hour: 'numeric', minute: '2-digit' } })
            : ''),
        },
        {
          token: `${CATEGORY_IDENTIFIERS.SESSION}.${SESSION_NOTE_FIELDS.ENTERED_START_TIME}`,
          id: SESSION_NOTE_FIELDS.ENTERED_START_TIME,
          label: this.$t('templates.session.enteredStartTime'),
          hint: null,
          category: CATEGORY_IDENTIFIERS.SESSION,
          type: 'time',
          prompt: true,
          deferPrompt: true,
          promptGroup: 'sessionTime',
          adjustGroupField: (value, fields) => (
            this.adjustGroupField(value, fields, SESSION_NOTE_FIELDS.ENTERED_FINISH_TIME)
          ),
          adjust: (value, props = {}) => {
            const { sessionActivity } = (props || {});

            return this.adjustEnteredStartTime(value, sessionActivity.startTimestamp);
          },
          warning: (value, session, sessionActivity, appointment) => {
            // Show warning if entered start time is more than 1 hour before/after scheduled start time
            if (!(isDate(value))
              || !(get(appointment, 'scheduledStartTimestamp') instanceof Timestamp)
              || (this.disableEditTimestamps(sessionActivity))) {
              return;
            }

            // Adjust scheduled date to match entered date because entered date is always today
            const scheduledStartDate = this.adjustDate(
              appointment.scheduledStartTimestamp.toDate(),
              { day: this.getDate(value), month: this.getMonth(value), year: this.getFullYear(value) },
            );
            const startTimeDiff = scheduledStartDate - value;

            if (Math.abs(startTimeDiff) > (60 * 60 * 1000)) {
              return this.$t(
                'templates.session.warningStartTime',
                {
                  scheduledStartTime: this.getLocaleTimeString(scheduledStartDate, {
                    options: { hour: 'numeric', minute: '2-digit' },
                  }),
                },
              );
            }
          },
          format: (ts) => (isDate(ts)
            ? this.getLocaleTimeString(ts, { options: { hour: 'numeric', minute: '2-digit' } })
            : ''),
        },
        {
          token: `${CATEGORY_IDENTIFIERS.SESSION}.${SESSION_NOTE_FIELDS.ENTERED_FINISH_TIME}`,
          id: SESSION_NOTE_FIELDS.ENTERED_FINISH_TIME,
          label: this.$t('templates.session.enteredFinishTime'),
          hint: null,
          category: CATEGORY_IDENTIFIERS.SESSION,
          type: 'time',
          prompt: true,
          deferPrompt: true,
          promptGroup: 'sessionTime',
          adjust: (endTimestamp, props) => {
            const { startTimestamp } = (props || {});

            return this.adjustEndTime(endTimestamp, startTimestamp);
          },

          warning: (value, session, sessionActivity, appointment) => {
            // Show warning if entered start time is more than 1 hour before/after scheduled start time
            if (!(isDate(value))
              || !(get(appointment, 'scheduledStartTimestamp') instanceof Timestamp)
              || (this.disableEditTimestamps(sessionActivity))) {
              return;
            }

            // Adjust scheduled date to match entered date because entered date is always today
            const scheduledStartDate = this.adjustDate(
              appointment.scheduledStartTimestamp.toDate(),
              { day: this.getDate(value), month: this.getMonth(value), year: this.getFullYear(value) },
            );

            const scheduledEndDate = this.addToDate(
              scheduledStartDate,
              { seconds: appointment.duration || 0 },
            );

            const endTimeDiff = scheduledEndDate - value;

            if (Math.abs(endTimeDiff) > (60 * 60 * 1000)) {
              return this.$t(
                'templates.session.warningEndTime',
                {
                  scheduledEndTime: this.getLocaleTimeString(scheduledEndDate, {
                    options: { hour: 'numeric', minute: '2-digit' },
                  }),
                },
              );
            }
          },
          format: (ts) => (isDate(ts)
            ? this.getLocaleTimeString(ts, { options: { hour: 'numeric', minute: '2-digit' } })
            : ''),
        },
        {
          token: 'session.sessionDuration',
          id: 'sessionDuration',
          label: this.$t('templates.session.duration'),
          hint: null,
          category: 'session',
          type: 'duration',
          prompt: true,
          deferPrompt: true,
          completable: true,

          format: (duration) => (isNumber(duration)
            ? this.formatDuration(duration)
            : duration),
        },
        {
          token: 'session.userName',
          id: 'userName',
          label: this.$t('templates.session.userName'),
          hint: null,
          category: 'session',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
        },
        {
          token: 'session.distance',
          id: 'distance',
          label: this.$t('templates.session.distance'),
          hint: null,
          category: 'session',
          type: 'number',
          prompt: true,
          deferPrompt: true,
          promptGroup: 'sessionTime',

          format: (distance) => (this.formatDistance(distance, this.connectedClinic.country)),
        },
        {
          token: 'appointment.scheduledStartDate',
          id: 'scheduledStartDate',
          label: this.$t('templates.appointment.scheduledStartDate'),
          hint: null,
          category: 'appointment',
          type: 'date',
          allowMultiLine: false,
          prompt: false,
        },
        {
          token: 'appointment.scheduledStartTime',
          id: 'scheduledStartTime',
          label: this.$t('templates.appointment.scheduledStartTime'),
          hint: null,
          category: 'appointment',
          type: 'time',
          allowMultiLine: false,
          prompt: false,
          format: (ts) => (isDate(ts)
            ? this.getLocaleTimeString(ts, { options: { hour: 'numeric', minute: '2-digit' } })
            : ''),

        },
        {
          token: 'appointment.scheduledFinishTime',
          id: 'scheduledFinishTime',
          label: this.$t('templates.appointment.scheduledFinishTime'),
          hint: null,
          category: 'appointment',
          type: 'time',
          allowMultiLine: false,
          prompt: false,
          format: (ts) => (isDate(ts)
            ? this.getLocaleTimeString(ts, { options: { hour: 'numeric', minute: '2-digit' } })
            : ''),

        },
        {
          token: 'appointment.placeOfService',
          id: 'placeOfService',
          label: this.$t('templates.appointment.placeOfService'),
          hint: null,
          category: 'appointment',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
          excludeIntegrations: ['aloha', 'vireo', 'amrex'],
        },
        {
          token: 'appointment.placeOfServiceAddress',
          id: 'placeOfServiceAddress',
          label: this.$t('templates.appointment.placeOfServiceAddress'),
          hint: null,
          category: 'appointment',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
          excludeIntegrations: ['vireo', 'amrex'],
        },
        {
          token: 'appointment.placeOfServiceCode',
          id: 'placeOfServiceCode',
          label: this.$t('templates.appointment.placeOfServiceCode'),
          hint: null,
          category: 'appointment',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
          integrationExclusive: [
            INTEGRATION_NAMES.ALOHA,
          ],
        },
        {
          token: 'appointment.placeOfServiceDescription',
          id: 'placeOfServiceDescription',
          label: this.$t('templates.appointment.placeOfServiceDescription'),
          hint: null,
          category: 'appointment',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
          integrationExclusive: [
            INTEGRATION_NAMES.ALOHA,
          ],
        },
        {
          token: 'appointment.serviceType',
          id: 'serviceType',
          label: this.$t('templates.appointment.serviceType'),
          hint: null,
          category: 'appointment',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
          excludeIntegrations: Object.values(INTEGRATION_NAMES),
        },
        {
          token: 'appointment.serviceName',
          id: 'serviceName',
          label: this.$t('templates.appointment.serviceName'),
          hint: null,
          category: 'appointment',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
          excludeIntegrations: ['vireo', 'amrex'],
        },
        {
          token: 'appointment.serviceCode',
          id: 'serviceCode',
          label: this.$t('templates.appointment.serviceCode'),
          hint: null,
          category: 'appointment',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
          excludeIntegrations: ['amrex'],
          hiddenFromSelection: true, // hides option from select but does not disable it, used to allow old templates
          // to function and, at the same time, hide this option for new templates
        },
        {
          token: 'appointment.serviceCodeV2',
          id: 'serviceCodeV2',
          label: this.$t('templates.appointment.serviceCode'),
          hint: null,
          category: 'appointment',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
          excludeIntegrations: ['amrex'],
        },
        {
          token: 'appointment.serviceCodeModifiers',
          id: 'serviceCodeModifiers',
          label: this.$t('templates.appointment.serviceCodeModifiers'),
          hint: null,
          category: 'appointment',
          type: 'select',
          allowMultiLine: false,
          prompt: false,
          format: (modifiers) => (isArray(modifiers) ? join(modifiers, ', ') : ''),
          excludeIntegrations: ['aloha', 'vireo', 'amrex'],
        },
        {
          token: 'appointment.diagnosisCode',
          id: 'diagnosisCode',
          label: this.$t('templates.appointment.diagnosisCode'),
          hint: null,
          category: 'appointment',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
          excludeIntegrations: [null, ...Object.values(INTEGRATION_NAMES).filter((name) => name !== 'lumary')],
        },
        {
          token: 'appointment.authorizationFundingSource',
          id: 'authorizationFundingSource',
          label: this.$t('templates.appointment.authorizationFundingSource'),
          hint: null,
          category: 'appointment',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
          excludeIntegrations: [
            null,
            ...Object.values(INTEGRATION_NAMES).filter((name) => name !== INTEGRATION_NAMES.CAMBER),
          ],
        },
        {
          token: 'appointment.authorizationRenderingProvider',
          id: 'authorizationRenderingProvider',
          label: this.$t('templates.appointment.authorizationRenderingProvider'),
          hint: null,
          category: 'appointment',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
          excludeIntegrations: [
            null,
            ...Object.values(INTEGRATION_NAMES).filter((name) => name !== INTEGRATION_NAMES.CAMBER),
          ],
        },
        {
          token: 'appointment.authorizationAuthorizedBCBA',
          id: 'authorizationAuthorizedBCBA',
          label: this.$t('templates.appointment.authorizationAuthorizedBCBA'),
          hint: null,
          category: 'appointment',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
          excludeIntegrations: [
            null,
            ...Object.values(INTEGRATION_NAMES).filter((name) => name !== INTEGRATION_NAMES.CAMBER),
          ],
        },
        {
          token: 'appointment.authorizationAuthorizedBCBACredentials',
          id: 'authorizationAuthorizedBCBACredentials',
          label: this.$t('templates.appointment.authorizationAuthorizedBCBACredentials'),
          hint: null,
          category: 'appointment',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
          excludeIntegrations: [
            null,
            ...Object.values(INTEGRATION_NAMES).filter((name) => name !== INTEGRATION_NAMES.CAMBER),
          ],
        },
        {
          token: 'appointment.authorizationMemberId',
          id: 'authorizationMemberId',
          label: this.$t('templates.appointment.authorizationMemberId'),
          hint: null,
          category: 'appointment',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
          excludeIntegrations: [
            null,
            ...Object.values(INTEGRATION_NAMES).filter((name) => name !== INTEGRATION_NAMES.CAMBER),
          ],
        },
        {
          token: 'user.name',
          id: 'name',
          label: this.$t('templates.user.name'),
          hint: null,
          category: 'user',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
        },
        {
          token: 'user.email',
          id: 'email',
          label: this.$t('templates.user.email'),
          hint: null,
          category: 'user',
          type: 'text',
          allowMultiLine: false,
          prompt: false,
        },

      ],

    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'connectedClinic', 'treatmentServices']),
    ...mapState(network, ['offlineMode']),

    /** @return {Field[]} */
    sessionNoteFieldPresets() {
      const integration = this.connectedClinic.integration || null;

      return chain(this.fixedSessionNoteFieldPresets)
        .reject((field) => (includes(field.excludeIntegrations, integration)))
        .reject((field) => isArray(field.integrationExclusive) && !includes(field.integrationExclusive, integration))
        .concat(this.clientDataFieldPresets)
        .concat(this.staffMemberDataFieldPresets)
        .value();
    }, // sessionNoteFieldPresets()

    /** @return {Field[]} */
    sessionNoteLayoutFieldsPreset() {
      return [
        {
          category: CATEGORY_IDENTIFIERS.SESSION_NOTE,
          token: 'sessionNote.currentPage',
          label: this.$t('templates.report.currentPage'),
          canEdit: false,
          id: 'currentPage',
        },
        {
          category: CATEGORY_IDENTIFIERS.SESSION_NOTE,
          token: 'sessionNote.pageCount',
          label: this.$t('templates.report.pageCount'),
          canEdit: false,
          id: 'pageCount',
        },
      ];
    }, // sessionNoteLayoutFieldsPreset()

    /** @return {FieldCategory} */
    sessionNoteLayoutFieldCategory() {
      return {
        categoryIdentifier: CATEGORY_IDENTIFIERS.SESSION_NOTE,
        categoryLabel: this.$t('templates.categories.sessionNotes'),
        fields: this.sessionNoteLayoutFieldsPreset,
      };
    }, // sessionNoteLayoutFieldCategory()

  },

  methods: {
    ...mapActions(auth, ['updateUserToken']),
    ...mapActions(broadcastChannel, ['sendBroadcastMessage']),
    ...mapActions(firebaseUploader, ['addToUploadQueue']),

    /**
     * @param {Field[]} fields
     * @return {FieldCategory}
     */
    sessionNoteFieldPresetToCategories(fields) {
      const categoryIdentifier = fields?.[0]?.category;

      return {
        categoryIdentifier,
        categoryLabel: this.sessionNoteFieldCategoryPresets.find((preset) => {
          return preset.categoryIdentifier === categoryIdentifier;
        })?.categoryLabelTranslation || categoryIdentifier,
        fields: sortBy(fields, ['canEdit', 'label']),
      };
    },

    /**
     * @param {Field} field
     * @return {Field}
     */
    getTokenField(field) {
      const token = (field.category === null)
        ? field.id
        : `${field.category}.${field.id}`;
      const tokenType = (field.type === 'sessionData') ? 'dataSection' : 'dynamicField';
      return {
        category: field.category,
        token,
        tokenType,
        label: field.label,
        canEdit: true,
        type: field.type,
        id: field.id,
      };
    }, // sessionNoteFieldPresetToCategories()

    filterPromptedTemplateFields(templateFields, isSessionInProgress = false) {
      return reject(templateFields, (token) => {
        const field = find(this.sessionNoteFieldPresets, { token });
        return get(field, 'prompt', null) === false;
      });
    }, // filterPromptedTemplateFields()

    compileSessionNoteInformation(session, client, sessionActivity, appointment, staffMember = null) {
      const {
        sessionStartTime: notesSessionStart,
        sessionFinishTime: notesSessionFinish,
      } = sessionActivity.sessionNotesFieldData?.session || {};
      const startTimestampNotes = notesSessionStart instanceof Timestamp
        ? notesSessionStart : sessionActivity.startTimestamp;
      const endTimestampNotes = notesSessionFinish instanceof Timestamp
        ? notesSessionFinish : sessionActivity.endTimestamp;

      const sessionDate = (sessionActivity.startTimestamp instanceof Timestamp)
        ? sessionActivity.startTimestamp.toDate()
        : null;
      const sessionStartTime = (startTimestampNotes instanceof Timestamp)
        ? startTimestampNotes.toDate()
        : null;
      const sessionFinishTime = (endTimestampNotes instanceof Timestamp)
        ? endTimestampNotes.toDate()
        : null;
      const enteredStartTimeField = get(
        sessionActivity,
        ['sessionNotesFieldData', 'session', 'enteredStartTime'],
        null,
      );
      const enteredStartTime = (enteredStartTimeField instanceof Timestamp)
        ? enteredStartTimeField.toDate()
        : null;
      const enteredFinishTimeField = get(
        sessionActivity,
        ['sessionNotesFieldData', 'session', 'enteredFinishTime'],
        null,
      );
      const enteredFinishTime = (enteredFinishTimeField instanceof Timestamp)
        ? enteredFinishTimeField.toDate()
        : null;
      const sessionDuration = (isDate(sessionStartTime) && isDate(sessionFinishTime))
        ? (sessionFinishTime - sessionStartTime) / 1000
        : null;
      const scheduledStartDate = (get(appointment, 'scheduledStartTimestamp') instanceof Timestamp)
        ? appointment.scheduledStartTimestamp.toDate()
        : null;
      const scheduledStartTime = (get(appointment, 'scheduledStartTimestamp') instanceof Timestamp)
        ? appointment.scheduledStartTimestamp.toDate()
        : null;
      const scheduledFinishTime = isDate(scheduledStartDate)
        ? this.addToDate(
          scheduledStartDate,
          { seconds: appointment.duration || 0 },
        )
        : null;

      let placeOfServiceName = null;
      let placeOfServiceAddress = null;

      if (appointment?.placeOfService) {
        const placeOfService = find(this.placesOfService, { id: appointment.placeOfService });
        placeOfServiceName = placeOfService?.placeOfServiceName || null;
        const defaultAddress = placeOfService?.addressSource === 'other'
          // 'other' = address entered per-appointment at scheduling time; it lives on
          // the appointment, not the place of service (mirrors formatPlaceOfServiceLabel).
          ? (appointment?.placeOfServiceAddress || null)
          : this.formatPlaceOfServiceAddress(
            placeOfService.customAddress?.address1,
            placeOfService.customAddress?.city,
            placeOfService.customAddress?.state,
            placeOfService.customAddress?.postalCode,
          );
        placeOfServiceAddress = this.getAppointmentPlaceOfServiceAddress(
          placeOfService?.addressSource,
          defaultAddress || null,
          client,
        );
      } else if (appointment?.placeOfServiceName) {
        // No native place of service code found, use the appointment's place of service (retrieved from integration)
        placeOfServiceName = appointment?.placeOfServiceName || null;
        placeOfServiceAddress = appointment?.placeOfServiceAddress || null;
      } else if (appointment?.location) {
        placeOfServiceAddress = appointment.location.InPerson?.Street1 || null;
      }

      const DELIMITER_POS_CODE = '-';

      const POSCode = appointment?.integrationAppointmentPOSCode || null;

      const placeOfServiceCode = POSCode ? POSCode.split(DELIMITER_POS_CODE)[0].trim() : null;
      const placeOfServiceDescription = POSCode ? POSCode.split(DELIMITER_POS_CODE)[1]?.trim() : null;

      let serviceCode = null;
      // new "serviceCode" prop, for everyone except Lumary it'll provide the same value
      // for Lumary this will now replace "serviceCode" and show only the number
      // old "serviceCode" is kept for existing templates
      let serviceCodeV2 = null;
      const treatmentService = find(this.treatmentServices, { id: get(appointment, 'treatmentService') });

      if (appointment?.integration === 'aloha') {
        const integrationServiceCode = get(appointment, 'integrationAppointmentServiceCode', null);
        serviceCode = integrationServiceCode ? integrationServiceCode.split(DELIMITER_POS_CODE)[0].trim() : null;
        serviceCodeV2 = serviceCode;
      } else if (appointment?.integration === 'lumary') {
        // old "serviceCode" was mapped to the "name" property, but now the "name" property is mapped to "serviceName"
        // old "serviceCode" === new "serviceName". new/refreshed appts will include "serviceName"
        // old appts will fallback to "serviceCode"
        serviceCode = appointment?.serviceName || appointment?.serviceCode || null;
        serviceCodeV2 = (appointment?.serviceName && appointment?.serviceCode) || null;
      } else {
        serviceCode = get(
          appointment,
          'serviceCode',
          get(treatmentService, 'serviceCode', null),
        );
        serviceCodeV2 = serviceCode;
      }

      const serviceCodeModifiers = get(
        appointment,
        'serviceCodeModifiers',
        get(treatmentService, 'modifiers', null),
      );
      const diagnosisCode = get(
        appointment,
        'diagnosisCode',
        get(treatmentService, 'diagnosisCode', null),
      );

      const staffMemberDataFields = this.getFormattedStaffMemberDataFields(staffMember);

      let serviceName = null;
      if (appointment?.integration === 'aloha') {
        serviceName = appointment.integrationAppointmentType;
      } else if (appointment?.integration === 'lumary') {
        serviceName = appointment?.serviceName || appointment?.serviceCode || null;
      } else {
        serviceName = treatmentService?.serviceName || null;
      }

      // session note info
      return {
        ...(client === null) ? {} : { client: this.getFormattedClientDataFields(client) },
        ...(staffMember === null) ? {} : { staffMember: { ...staffMember, ...staffMemberDataFields } },
        session: {
          sessionName: get(session, 'sessionName'),
          sessionActivityId: get(sessionActivity, 'id'),
          sessionDate,
          sessionStartTime,
          sessionFinishTime,
          enteredStartTime,
          enteredFinishTime,
          sessionDuration,
          distance: (sessionActivity.distance === '' ? null : sessionActivity.distance)
            ?? sessionActivity.sessionNotesFieldData?.session?.distance,
          userName: get(sessionActivity, 'userName'),
        },
        appointment: {
          scheduledStartDate,
          scheduledStartTime,
          scheduledFinishTime,
          placeOfService: placeOfServiceName || null,
          placeOfServiceAddress: placeOfServiceAddress || null,
          placeOfServiceCode,
          placeOfServiceDescription,
          serviceType: get(treatmentService, 'serviceType') || null,
          serviceName,
          serviceTaxonomy: get(treatmentService, 'taxonomy') || null,
          serviceCode,
          serviceCodeV2,
          serviceCodeModifiers,
          diagnosisCode,
          authorizationFundingSource: appointment?.authorizationFundingSource ?? null,
          authorizationRenderingProvider: appointment?.authorizationRenderingProvider ?? null,
          authorizationAuthorizedBCBA: appointment?.authorizationAuthorizedBCBA ?? null,
          authorizationAuthorizedBCBACredentials: appointment?.authorizationAuthorizedBCBACredentials ?? null,
          authorizationMemberId: appointment?.authorizationMemberId ?? null,
        },
        user: {
          name: get(this.userDetails, 'name'),
          email: get(this.userDetails, 'email'),
        },
      };
    }, // compileSessionNoteInformation()

    getUpdatedSessionNoteFields(sessionActivity, sessionNoteFields) {
      const updateSessionActivity = {};
      const updatedSessionNoteFields = cloneDeep(sessionNoteFields);

      if (!(get(sessionActivity, 'startTimestamp') instanceof Timestamp)
        || !(get(sessionActivity, 'endTimestamp') instanceof Timestamp)
      ) {
        return { updatedSessionNoteFields, updateSessionActivity };
      }

      let sessionActivityStartTimestamp = get(sessionActivity, 'startTimestamp').toDate();
      let sessionActivityEndTimestamp = get(sessionActivity, 'endTimestamp').toDate();

      let updatedStartTime = sessionNoteFields.session?.enteredStartTime || sessionNoteFields.session?.sessionStartTime;

      if (updatedStartTime instanceof Timestamp) {
        updatedStartTime = updatedStartTime.toDate();
      }

      if (isDate(updatedStartTime)) {
        // Adjust session activity start time
        const startTime = this.formatDate(updatedStartTime, 'HH:mm');
        const startTimestampString = `${this.formatDate(sessionActivityStartTimestamp, 'yyyy-MM-dd')} ${startTime}`;

        sessionActivityStartTimestamp = this.extractDate(startTimestampString, 'yyyy-MM-dd HH:mm');
        updateSessionActivity.startTimestamp = Timestamp.fromDate(sessionActivityStartTimestamp);
        sessionActivityEndTimestamp = this.adjustDate(sessionActivityEndTimestamp, {
          second: this.getSeconds(sessionActivityStartTimestamp),
          millisecond: this.getMilliseconds(sessionActivityStartTimestamp),
        });

        if (sessionActivityEndTimestamp <= sessionActivityStartTimestamp) {
          /** Sometimes comparison of timestamps with the same HH:mm lead to invalid result because one of them could
           * still have seconds portion. In previous implementation we added 1 day in such case and we could
           * see duration as ~23:59:34 even if we compare the same hours/minutes timestamps
           * */
          const duration = this.getDateDiff(sessionActivityEndTimestamp, sessionActivityStartTimestamp, 'seconds');
          const options = duration === 0 ? { minutes: 1 } : { days: 1 };
          // Adjust session finish time
          sessionActivityEndTimestamp = this.addToDate(sessionActivityEndTimestamp, options);

          if (isDate(sessionNoteFields?.session?.enteredStartTime)) {
            updatedSessionNoteFields.session.enteredStartTime = sessionActivityStartTimestamp;
          }
        }
        updatedSessionNoteFields.session.sessionStartTime = sessionActivityStartTimestamp;
        updatedSessionNoteFields.session.sessionFinishTime = sessionActivityEndTimestamp;
        updateSessionActivity.endTimestamp = Timestamp.fromDate(sessionActivityEndTimestamp);

        updatedSessionNoteFields.session.sessionDuration = (
          sessionActivityEndTimestamp - sessionActivityStartTimestamp
        ) / 1000;
      }

      let updatedEndTime = sessionNoteFields.session?.enteredFinishTime || sessionNoteFields.session?.sessionFinishTime;

      if (updatedEndTime instanceof Timestamp) {
        updatedEndTime = updatedEndTime.toDate();
      }

      if (isDate(updatedEndTime)) {
        const endTime = this.formatDate(updatedEndTime, 'HH:mm');
        const endTimestampString = `${this.formatDate(sessionActivityStartTimestamp, 'yyyy-MM-dd')} ${endTime}`;

        sessionActivityEndTimestamp = this.extractDate(endTimestampString, 'yyyy-MM-dd HH:mm');
        sessionActivityEndTimestamp = this.adjustDate(sessionActivityEndTimestamp, {
          second: this.getSeconds(sessionActivityStartTimestamp),
          millisecond: this.getMilliseconds(sessionActivityStartTimestamp),
        });

        if (sessionActivityEndTimestamp <= sessionActivityStartTimestamp) {
          /** Sometimes comparison of timestamps with the same HH:mm lead to invalid result because one of them could
           * still have seconds portion. In previous implementation we added 1 day in such case and we could
           * see duration as ~23:59:34 even if we compare the same hours/minutes timestamps
           * */
          const duration = this.getDateDiff(sessionActivityEndTimestamp, sessionActivityStartTimestamp, 'seconds');

          const options = duration === 0 ? { minutes: 1 } : { days: 1 };
          // Adjust session finish time
          sessionActivityEndTimestamp = this.addToDate(sessionActivityEndTimestamp, options);

          if (isDate(sessionNoteFields?.session?.enteredFinishTime)) {
            updatedSessionNoteFields.session.enteredFinishTime = sessionActivityEndTimestamp;
          }
        }
        updatedSessionNoteFields.session.sessionFinishTime = sessionActivityEndTimestamp;
        updateSessionActivity.endTimestamp = Timestamp.fromDate(sessionActivityEndTimestamp);

        updatedSessionNoteFields.session.sessionDuration = (
          sessionActivityEndTimestamp - sessionActivityStartTimestamp
        ) / 1000;
      }

      const updatedDistance = get(sessionNoteFields, ['session', 'distance']);
      if (isNumber(updatedDistance)) {
        updateSessionActivity.distance = updatedDistance;
      }

      return { updatedSessionNoteFields, updateSessionActivity };
    }, // getUpdatedSessionNoteFields()

    getSessionNotesFieldDataFromSessionActivity(sessionActivity) {
      return chain(sessionActivity)
        .get('sessionNotesFieldData', null)
        .mapValues((groupItems) => {
          if (!isObject(groupItems) || isArray(groupItems) || (groupItems instanceof Timestamp) || isDate(groupItems)) {
            if (chain(groupItems).get('toDate').isFunction().value()) {
              return groupItems.toDate();
            }
            return groupItems;
          }
          return mapValues(groupItems, (item) => {
            if (chain(item).get('toDate').isFunction().value()) {
              return item.toDate();
            }
            return item;
          });
        })
        .value();
    }, // getSessionNotesFieldDataFromSessionActivity()

    async requestSignature(session, signatory, sessionActivity) {
      if ((signatory.userName === null) || !session) {
        return;
      }

      const messagesDb = new MessagesDB();
      const message = this.$t('signature.textRequestedSignature', {
        userName: `@${this.userDetails.name}`,
        requestedUserName: `@${signatory.userName}`,
      });
      const messageHtml = `<p>${this.$t('signature.textRequestedSignature', {
        userName: `<span data-type="mention" class="user-mention" data-id="${this.userDetails.userId
        }" data-label="${this.userDetails.name}">@${this.userDetails.name}</span>`,
        requestedUserName: `<span data-type="mention" class="user-mention" data-id="${signatory.userId
        }" data-label="${signatory.userName}">@${signatory.userName}</span>`,
      })}</p>`;

      const messageObj = {
        author: this.userDetails.userId,
        authorName: this.userDetails.name,
        thread: `session_${session.id}`,
        threadSubPath: session.id,
        origin: 'session',
        session: session.id,
        sessionTimeOffset: null,
        message,
        messageHtml,
        clinic: this.userDetails.clinic,
        client: session.client,
        type: 'signatureRequest',
        sessionActivityId: sessionActivity.id,
        signatoryId: signatory.uid,
        requestedUser: signatory.userId,
        resolved: false,
      };
      await messagesDb.create(messageObj);
    }, // requestSignature()

    async saveSignature(sessionActivity, signatureData, signatureDetails, callbackFunc = null) {
      const signature = {
        ...signatureDetails,
        timestamp: new Date(),
        createdBy: this.userDetails.userId,
      };

      const callback = async (signatureRef) => {
        if (this.offlineMode) {
          // set base64 to url
          signature.url = signatureData;
          // save the id of the info saved in IDB to be able to replace url later
          signature.id = signatureRef.id;
        } else {
          signature.url = await getDownloadURL(signatureRef);
        }

        if (isFunction(callbackFunc)) {
          const sessionActivityChanges = {
            signatures: arrayUnion(signature),
          };
          await callbackFunc(sessionActivityChanges, sessionActivity, signature);
        }
      };

      const { clinic, id } = sessionActivity;
      const destinationPath = `/clinics/${clinic}/sessionActivities/${id}/signatures/`;

      await this.uploadSignature(signatureData, destinationPath, sessionActivity.session || null, callback);
    }, // saveSignature()

    async uploadSignature(signature, destinationPath, sessionId, callback) {
      const uploadInfo = {
        destinationPath,
        destinationFilename: this.createUniqueSignatureFilename(),
        isBase64: true,
        file: signature,
        metadata: {
          contentType: 'image/png',
          customMetadata: {
            signature: true,
            session: sessionId,
          },
        },
        callback,
      };

      if (this.offlineMode) {
        uploadInfo.offline = true;
      }

      try {
        await this.addToUploadQueue(uploadInfo);
      } catch (err) {
        await this.callWithRetries(
          () => {
            // We should create a new unique filename for the signature in order to avoid overwriting the existing one
            // if it was uploaded successfully but there was an error due to some internet connection issues
            return this.addToUploadQueue({
              ...uploadInfo,
              destinationFilename: this.createUniqueSignatureFilename(),
            });
          },
          this.$t('sessions.sessionCard.warningErrorSavingSignature', { error: err.message }),
          this.$t('sessions.sessionCard.notifySignatureSuccessfullySaved'),
        );
      }
    }, // uploadSignature()

    /**
     * Create a signature's filename contains a random UID
     * @return {string}
     */
    createUniqueSignatureFilename() {
      return `signature-${uid()}.png`;
    }, // createUniqueSignatureFilename()

    /**
     * @todo move it into `AppUtils` when it is to be used throughout the app
     *
     * @param {() => Promise<any>} action - function to be called multiple times
     * @param {string} [errMessage]
     * @param {string} [successMessage]
     * @param {number} [retries] - number of retries to be made
     * @return {Promise<void>}
     */
    async callWithRetries(action, errMessage, successMessage, retries) {
      let retriesLeft = retries || 5;
      let dismissNotification;
      const retryingText = this.$t('common.retryMessage', { retries: retriesLeft });
      const message = errMessage ? `${errMessage} ${retryingText}` : retryingText;
      const onStopRetrying = () => {
        retriesLeft = 0;
        if (typeof dismissNotification === 'function') {
          dismissNotification();
        }
      };

      while (retriesLeft > 0) {
        dismissNotification = this.notifyWarning(message, {
          progress: false,
          spinner: true,
          timeout: 0,
          actions: [
            {
              label: 'Cancel retrying',
              handler: onStopRetrying,
            },
          ],
        });

        try {
          // eslint-disable-next-line no-await-in-loop
          await action();

          // successful attempt
          this.notifySuccess(successMessage);
          onStopRetrying();
        } catch (nextError) {
          if (retriesLeft === 1) {
            logEvent(nextError);
            this.notifyNegative(nextError.message);
            onStopRetrying();
          }
        } finally {
          retriesLeft -= 1;
        }
      }
    }, // callWithRetries()

    /**
     * @param {SavingNotesOptions} [savingOptions]
     * @return {Promise<string | null>} notes with uploaded images if {@link updatedNotes} was provided
     */
    async processSavingNotesImagesInStorage(savingOptions = {
      draftSave: false,
      hardUpdate: false,
      originalNotes: null,
      updatedNotes: null,
      sessionActivityId: null,
    }) {
      const loaderName = savingOptions.draftSave ? 'isSavingDraft' : 'isSaving';
      this[loaderName] = true;

      try {
        await this.uploadAndEmbedBase64NotesImages(savingOptions);
        await this.updateStorageImagesMetadata(savingOptions);

        if (this.offlineMode) {
          await this.findAndRemoveDeletedImagesFromUploadingQueue(savingOptions);
        }
      } finally {
        this[loaderName] = false;
      }

      return savingOptions.updatedNotes;
    }, // processSavingNotesImagesInStorage()

    /** @param {SavingNotesOptions} [savingOptions] */
    async uploadAndEmbedBase64NotesImages(savingOptions) {
      const imageData = {
        collectionName: SESSION_ACTIVITY_COLLECTION,
        itemId: savingOptions.sessionActivityId,
      };

      if (this.offlineMode) {
        const notes = savingOptions.updatedNotes
          ? await replaceLocalImagesWithStorage(savingOptions.updatedNotes)
          : await replaceLocalImagesWithStorage(this.editNotes);

        const notesWithStorageImages = await this.replaceImageSources(notes, imageData);

        if (savingOptions.updatedNotes) {
          // eslint-disable-next-line no-param-reassign
          savingOptions.updatedNotes = notesWithStorageImages;
        } else {
          this.editNotes = notesWithStorageImages;
        }

        return;
      }

      if (savingOptions.updatedNotes) {
        // eslint-disable-next-line no-param-reassign
        savingOptions.updatedNotes = await this.replaceImageSources(savingOptions.updatedNotes, imageData);
      } else {
        const notesAfterReplacement = await this.replaceImageSources(this.editNotes, imageData);

        if (this.editNotes !== notesAfterReplacement) {
          this.editNotes = notesAfterReplacement;
        }
      }
    }, // uploadAndEmbedBase64NotesImages()

    /** @param {SavingNotesOptions} [savingOptions] */
    async updateStorageImagesMetadata(
      { originalNotes = null, updatedNotes = null, hardUpdate = false, sessionActivityId = null },
    ) {
      const initialNotes = originalNotes || this.sessionActivity?.notes || '';
      const newNotes = updatedNotes || this.editNotes;

      // eslint-disable-next-line prefer-const
      let { updatedNotesImagesPaths, imagesToDeletePaths, newImagesPaths } = this.findImagesPathsToDeleteAndUpdate(
        initialNotes,
        newNotes,
      );

      const deleteActions = map(
        imagesToDeletePaths,
        (path) => this.setImageFileMetadata(path, { [sessionActivityId]: null }),
      );
      const updateActions = map(
        (hardUpdate ? updatedNotesImagesPaths : newImagesPaths),
        (path) => this.setImageFileMetadata(path, { [sessionActivityId]: SESSION_ACTIVITY_COLLECTION }),
      );
      const allAction = concat(deleteActions, updateActions);

      if (!isEmpty(allAction)) {
        await AppUtils.allSettled(allAction, (err) => this.notifyNegative(err.message));
      }

      if (this.offlineMode) {
        return;
      }

      return this.deleteUnusedImagesAndClearDeleteQueue();
    }, // updateStorageImagesMetadata()

    /**
     * @param {SavingNotesOptions} savingOptions
     */
    async findAndRemoveDeletedImagesFromUploadingQueue({ originalNotes, updatedNotes }) {
      const initialNotes = originalNotes || this.sessionActivity?.notes || '';
      const newNotes = updatedNotes || this.editNotes;

      const deletedImagesIds = await findImagesToDeleteFromUploadingQueue(initialNotes, newNotes);
      if (!deletedImagesIds.length) {
        return;
      }

      const filesUploadingDb = new GenericIndexedDb(FILES_TO_UPLOAD_COLLECTION);
      const deleteActions = deletedImagesIds.map((docId) => {
        return filesUploadingDb.delete(docId, true);
      });

      return AppUtils.allSettled(deleteActions);
    },

    /**
     * Compare images paths in initial and new notes and return differences
     *
     * @param {string} initialNotes
     * @param {string} newNotes
     * @return {{imagesToDeletePaths: string[], newImagesPaths: string[], updatedNotesImagesPaths: string[]}}
     */
    findImagesPathsToDeleteAndUpdate(initialNotes, newNotes) {
      const originalNotesImagesPaths = this.findStorageImagesPaths(initialNotes);
      const updatedNotesImagesPaths = this.findStorageImagesPaths(newNotes);

      return {
        updatedNotesImagesPaths,
        imagesToDeletePaths: without(originalNotesImagesPaths, ...updatedNotesImagesPaths),
        newImagesPaths: without(updatedNotesImagesPaths, ...originalNotesImagesPaths),
      };
    }, // findImagesPathsToDeleteAndUpdate()

    disableEditTimestamps(sessionActivity) {
      return !(
        (sessionActivity.user === this.userDetails.userId)
        && this.userHasPrivilege('sessionActivities/editOwnActivityTimeFrame')
      ) && !(
        (sessionActivity.user !== this.userDetails.userId)
        && this.userHasPrivilege('sessionActivities/editAssociateActivityTimeFrame')
      );
    }, // disableEditTimestamps()

    /* TODO Move render notes logic to separate composable so we can use getRenderedNotesWithChangeRequest,
      convertHtmlToTokensBeforeRender, getFieldTransformations in the sessionActivityChangeRequest composable
     */
    getRenderedNotesWithChangeRequest(sessionActivity, updatedSessionNoteFields, changeRequestKeys) {
      return this.renderTemplate(
        this.convertNotesWithChangeRequest(changeRequestKeys, sessionActivity.notes),
        updatedSessionNoteFields,
        this.getChangeRequestFieldTransformations(),
      );
    }, // getRenderedNotesWithChangeRequest

    convertNotesWithChangeRequest(changeRequestKeys, notes) {
      const changeRequestTokenFields = [];

      changeRequestKeys.forEach((key) => {
        switch (key) {
          case 'startTimestamp':
            changeRequestTokenFields.push(
              this.fixedSessionNoteFieldPresets.find((field) => field.id === 'sessionStartTime'),
            );
            break;
          case 'endTimestamp':
            changeRequestTokenFields.push(
              this.fixedSessionNoteFieldPresets.find((field) => field.id === 'sessionFinishTime'),
            );
            break;
          case 'sessionDuration':
            changeRequestTokenFields.push(
              this.fixedSessionNoteFieldPresets.find((field) => field.id === 'sessionDuration'),
            );
            break;
          default:
            break;
        }
      });

      return this.convertHtmlToTokens(changeRequestTokenFields, notes);
    }, // convertNotesWithChangeRequest

    getChangeRequestFieldTransformations() {
      const fieldTransformations = chain(this.fixedSessionNoteFieldPresets)
        .reject((field) => isEmpty(field.outputFormat))
        .map((field) => ([field.token, replace(field.outputFormat, /%TOKEN%/g, field.token)]))
        .fromPairs()
        .value();
      const formatTransformations = chain(this.fixedSessionNoteFieldPresets)
        .filter((field) => isFunction(field.format))
        .map((field) => ([field.token, field.format]))
        .fromPairs()
        .value();

      return {
        ...fieldTransformations,
        ...formatTransformations,
      };
    }, // getChangeRequestFieldTransformations

    adjustEnteredStartTime(value, activityStartTimestamp) {
      if (!(value instanceof Timestamp) || !(activityStartTimestamp instanceof Timestamp)) {
        return value;
      }

      let baseDate = new Date(activityStartTimestamp.toMillis());
      const valueDate = value.toDate();
      baseDate = this.adjustDate(baseDate, {
        hour: this.getHours(valueDate),
        minute: this.getMinutes(valueDate),
        second: this.getSeconds(valueDate),
        millisecond: this.getMilliseconds(valueDate),
      });

      return Timestamp.fromDate(baseDate);
    }, // adjustEnteredStartTime()

    adjustGroupField(value, fields, affectedField) {
      const field = fields.find(({ id }) => id === affectedField);
      if (!field || !field.value) {
        return null;
      }

      const changedValue = field.adjust(
        Timestamp.fromDate(field.value),
        {
          startTimestamp: Timestamp.fromDate(value),
        },
      );

      return {
        adjustedField: field,
        changedValue,
      };
    }, // adjustGroupField

    adjustEndTime(endTimestamp, startTimestamp) {
      if (!(endTimestamp instanceof Timestamp) || !(startTimestamp instanceof Timestamp)) {
        return endTimestamp;
      }

      const duration = endTimestamp.toMillis() - startTimestamp.toMillis();

      if (duration <= MS_IN_24_HOURS && duration > 0) {
        return endTimestamp;
      }

      // Adjust the finish time to fit within 24 hours of the start time
      // If the finish time is earlier than the start time, set it to the start time + 1 day
      let newEndDate = startTimestamp.toDate();
      const endDate = endTimestamp.toDate();
      newEndDate = this.adjustDate(newEndDate, {
        hour: this.getHours(endDate),
        minute: this.getMinutes(endDate),
        second: this.getSeconds(endDate),
        millisecond: this.getMilliseconds(endDate),
      });

      return this.adjustToDate(
        Timestamp.fromDate(newEndDate),
        startTimestamp,
      );
    }, // adjustEndTime
  },
};
